/**
 * Audio-Encoder Utilities — wandeln einen `AudioBuffer` (z. B. das Ergebnis
 * eines Mikrofon-Recordings) in WAV- oder MP3-Blobs um.
 *
 * Wir bewusst Browser-only ohne Server-Roundtrip, damit:
 *  1) der User sofort einen Download bekommt,
 *  2) wir die Datei mit demselben Encoding ins Audio-Library-Backend
 *     hochladen können (WAV ist verlustfrei → ideal für VO),
 *  3) keine FFmpeg-WASM-Mega-Dependency nötig wird.
 *
 * Für MP3 nutzen wir `@breezystack/lamejs` (kleiner Wrapper um die klassische
 * lamejs-Library) — reines JS, keine WASM, ~80kb gzipped.
 */
import { Mp3Encoder } from "@breezystack/lamejs";

/**
 * Decodiert ein beliebiges Audio-Blob (z. B. webm/opus von der MediaRecorder
 * API oder eine gerade hochgeladene MP3) in einen AudioBuffer. Verwendet
 * einen frisch instanziierten `OfflineAudioContext` ist nicht nötig — der
 * normale AudioContext reicht und arbeitet schneller.
 */
export async function decodeBlob(blob: Blob): Promise<AudioBuffer> {
  const arrayBuf = await blob.arrayBuffer();
  // Eigener AudioContext pro Decode → garantiert Cleanup, vermeidet
  // Konflikte mit dem geteilten Waveform-Cache-Context.
  const Ctor = (window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as
    | typeof AudioContext
    | undefined;
  if (!Ctor) throw new Error("WebAudio nicht verfügbar (AudioContext fehlt).");
  const ac = new Ctor();
  try {
    return await new Promise<AudioBuffer>((resolve, reject) => {
      ac.decodeAudioData(arrayBuf.slice(0), resolve, reject);
    });
  } finally {
    void ac.close().catch(() => undefined);
  }
}

/**
 * Encodiert einen AudioBuffer in einen WAV-Blob (PCM 16-bit, kanal-treu).
 * Optimiert für Voiceover: minimaler Code (nur RIFF-Header + interleaved
 * PCM), keine Abhängigkeiten, ~Echtzeit auch für mehrminütige Aufnahmen.
 */
export function encodeWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2; // PCM 16-bit
  const length = buffer.length;
  const dataSize = length * numChannels * bytesPerSample;
  const totalSize = 44 + dataSize;

  const out = new ArrayBuffer(totalSize);
  const view = new DataView(out);
  let offset = 0;

  const writeStr = (s: string): void => {
    for (let i = 0; i < s.length; i += 1) {
      view.setUint8(offset, s.charCodeAt(i));
      offset += 1;
    }
  };

  // RIFF chunk descriptor
  writeStr("RIFF");
  view.setUint32(offset, totalSize - 8, true); offset += 4;
  writeStr("WAVE");

  // "fmt " sub-chunk
  writeStr("fmt ");
  view.setUint32(offset, 16, true); offset += 4;          // sub-chunk size
  view.setUint16(offset, 1, true); offset += 2;           // PCM
  view.setUint16(offset, numChannels, true); offset += 2;
  view.setUint32(offset, sampleRate, true); offset += 4;
  view.setUint32(offset, sampleRate * numChannels * bytesPerSample, true); offset += 4; // byte rate
  view.setUint16(offset, numChannels * bytesPerSample, true); offset += 2; // block align
  view.setUint16(offset, 8 * bytesPerSample, true); offset += 2;           // bits per sample

  // "data" sub-chunk
  writeStr("data");
  view.setUint32(offset, dataSize, true); offset += 4;

  // Interleaved samples (Float32 → Int16)
  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c += 1) channels.push(buffer.getChannelData(c));

  for (let i = 0; i < length; i += 1) {
    for (let c = 0; c < numChannels; c += 1) {
      let sample = channels[c][i];
      // Clamp + Float→Int16 (Roundtrip-treu nahe an −1..+1)
      sample = Math.max(-1, Math.min(1, sample));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return new Blob([out], { type: "audio/wav" });
}

/**
 * Encodiert einen AudioBuffer als MP3-Blob via lamejs. Bitrate konfigurierbar
 * (default 192 kbps — guter Kompromiss aus Sprache-Klarheit + Dateigröße).
 *
 * Hinweis: lamejs kennt nur Mono + Stereo. Mehrkanaliges Quellmaterial wird
 * vor dem Encoding zu Stereo gemischt.
 */
export function encodeMp3(buffer: AudioBuffer, bitrateKbps = 192): Blob {
  const sampleRate = buffer.sampleRate;
  const numChannels = Math.min(2, buffer.numberOfChannels);
  const encoder = new Mp3Encoder(numChannels, sampleRate, bitrateKbps);

  // lamejs erwartet Int16-Samples per Channel, nicht Float.
  const left = floatToInt16(buffer.getChannelData(0));
  const right = numChannels === 2
    ? floatToInt16(buffer.getChannelData(Math.min(1, buffer.numberOfChannels - 1)))
    : null;

  const chunks: Uint8Array[] = [];
  const blockSize = 1152;
  for (let i = 0; i < left.length; i += blockSize) {
    const lc = left.subarray(i, i + blockSize);
    const rc = right ? right.subarray(i, i + blockSize) : null;
    const enc = rc ? encoder.encodeBuffer(lc, rc) : encoder.encodeBuffer(lc);
    if (enc.length > 0) chunks.push(new Uint8Array(enc));
  }
  const flush = encoder.flush();
  if (flush.length > 0) chunks.push(new Uint8Array(flush));

  return new Blob(chunks, { type: "audio/mpeg" });
}

function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    let s = input[i];
    if (s > 1) s = 1;
    else if (s < -1) s = -1;
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

/** Triggert einen Datei-Download im Browser. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
